import { AlertTriangle, Box, Check, Clock, DollarSign, UserCheck } from 'lucide-react';
import { Badge, StatCard, StatusBadge } from '../components/shared/UI';
import { formatCurrency, formatDate } from '../lib/format';

const DashboardModule = ({ stats, loading, onViewTransaction }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in">
      <div>
        <h2 className="text-3xl font-heading font-bold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground mt-1">Ringkasan bisnis Anda hari ini</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Penjualan" 
          value={formatCurrency(stats.totalSales)} 
          icon={<DollarSign className="text-emerald-600" size={24} />} 
          color="bg-emerald-50"
          testId="stat-total-sales"
        />
        <StatCard 
          title="Pending" 
          value={formatCurrency(stats.totalPending || 0)} 
          icon={<Clock size={24} style={{color: '#FFB821'}} />} 
          color="bg-[#FFB82115]"
          testId="stat-pending"
        />
        <StatCard 
          title="Total Produk" 
          value={stats.totalProducts} 
          subtitle={`${stats.lowStockCount} stok rendah`}
          icon={<Box size={24} style={{color: '#0366D6'}} />} 
          color="bg-[#0366D610]"
          testId="stat-total-products"
        />
        <StatCard 
          title="Total Pelanggan" 
          value={stats.totalCustomers} 
          icon={<UserCheck size={24} style={{color: '#6571FF'}} />} 
          color="bg-[#6571FF10]"
          testId="stat-total-customers"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-heading font-semibold text-lg mb-4">Transaksi Terbaru</h3>
          {stats.recentTransactions?.length === 0 ? (
            <p className="text-muted-foreground text-sm">Belum ada transaksi.</p>
          ) : (
            <div className="space-y-1">
              {stats.recentTransactions?.map(t => (
                <div 
                  key={t.id} 
                  className="flex justify-between items-center py-3 px-3 -mx-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => onViewTransaction && onViewTransaction(t)}
                  data-testid={`recent-tx-${t.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Badge type={t.type} />
                    <div>
                      <p className="font-medium text-sm">{t.customerName}</p>
                      <p className="text-xs text-muted-foreground">#{t.id} &bull; {formatDate(t.date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-mono font-semibold text-sm">{formatCurrency(t.total)}</span>
                    <div className="mt-1">
                      <StatusBadge status={t.status || 'draft'} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-6">
          <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            Peringatan Stok Rendah
          </h3>
          {stats.lowStockProducts?.length === 0 ? (
            <div className="flex items-center gap-2 text-emerald-600 text-sm">
              <Check size={16} /> Semua stok dalam kondisi normal.
            </div>
          ) : (
            <div className="space-y-1">
              {stats.lowStockProducts?.map(p => (
                <div key={p.id} className="flex justify-between items-center py-3 px-3 -mx-3 rounded-lg hover:bg-slate-50">
                  <div>
                    <p className="font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.category}</p>
                  </div>
                  <span className="font-mono font-bold text-rose-600">
                    {p.stock} {p.unit}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default DashboardModule;
